import { cn } from '@amore/ui/cn'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
} from '@amore/ui/components/combobox'
import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Trash2, UserRound } from 'lucide-react'
import { useRef, useState } from 'react'
import { newId } from '@/lib/format'
import { usePrototype } from '@/mock/store'
import type { CeremonyRole } from '@/mock/types'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { ceremonyPeople, personKey } from './step-model'

export function CeremonyRoleSheet({ role, onClose }: { role?: CeremonyRole; onClose: () => void }) {
  const { data, canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const [title, setTitle] = useState(role?.title ?? '')
  const [person, setPerson] = useState(role?.person ?? null)
  const [query, setQuery] = useState('')
  const [titleError, setTitleError] = useState<string>()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const options = ceremonyPeople(data)
  const selectedKey = person ? personKey(person) : 'none'
  const selected = options.find((option) => personKey(option.person) === selectedKey)
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase('cs')
  const needle = normalize(query)
  const matches = options.filter((option) =>
    normalize(`${option.name} ${option.category}`).includes(needle),
  )
  const dirty =
    canEdit &&
    (title !== (role?.title ?? '') ||
      selectedKey !== (role?.person ? personKey(role.person) : 'none'))

  const save = () => {
    if (!canEdit) return
    if (!title.trim()) {
      setTitleError('Doplňte název role.')
      titleRef.current?.focus()
      return
    }
    const saved: CeremonyRole = {
      id: role?.id ?? newId('ceremony-role'),
      title: title.trim(),
      person,
    }
    update((current) => ({
      ...current,
      ceremony: {
        ...current.ceremony,
        roles: role
          ? current.ceremony.roles.map((item) => (item.id === role.id ? saved : item))
          : [...current.ceremony.roles, saved],
      },
    }))
    showToast({
      title: role ? 'Role uložena' : 'Role přidána',
      description: saved.title,
      tone: 'success',
    })
    onClose()
  }

  const remove = () => {
    if (!canEdit || !role) return
    update((current) => ({
      ...current,
      ceremony: {
        ...current.ceremony,
        roles: current.ceremony.roles.filter((item) => item.id !== role.id),
      },
    }))
    showToast({ title: 'Role smazána', description: role.title })
    onClose()
  }

  return (
    <>
      <DetailSheet
        open
        eyebrow="Lidé a role"
        title={role?.title || 'Nová role'}
        dirty={dirty}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        titleContent={
          <div className="space-y-1">
            <textarea
              ref={titleRef}
              value={title}
              rows={1}
              maxLength={150}
              readOnly={!canEdit}
              data-autofocus={(!role && canEdit) || undefined}
              aria-label="Název role"
              aria-invalid={Boolean(titleError)}
              aria-describedby={titleError ? 'ceremony-role-title-error' : undefined}
              placeholder="Název role"
              onChange={(event) => {
                setTitle(event.target.value)
                setTitleError(undefined)
              }}
              className={cn(
                'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
                canEdit && 'hover:bg-muted/60 focus:bg-muted/60',
              )}
            />
            {titleError ? (
              <p
                id="ceremony-role-title-error"
                role="alert"
                className="text-sm text-danger-foreground"
              >
                {titleError}
              </p>
            ) : null}
          </div>
        }
        destructiveAction={
          canEdit && role
            ? {
                label: 'Smazat roli',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
        primaryAction={
          canEdit ? { label: 'Uložit', onClick: () => formRef.current?.requestSubmit() } : undefined
        }
      >
        <FormLayout
          ref={formRef}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
          className="pt-1"
        >
          <Field
            controlId="ceremony-role-person"
            label={
              <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
                <UserRound className="size-4 shrink-0" aria-hidden="true" />
                Má na starosti
              </span>
            }
          >
            <Combobox
              name="person"
              value={selectedKey}
              displayValue={selected?.name ?? (person ? 'Dříve přiřazený člověk' : '')}
              query={query}
              onQueryChange={setQuery}
              onValueChange={(key) => {
                if (key === 'none') setPerson(null)
                else {
                  const option = options.find((option) => personKey(option.person) === key)
                  if (option) setPerson(option.person)
                }
              }}
              disabled={!canEdit}
            >
              <ComboboxInput
                variant="soft"
                className="h-10 border-transparent bg-muted/60 hover:bg-muted"
                placeholder="Zatím nepřiřazeno"
              />
              <ComboboxContent>
                <ComboboxList>
                  {!query ? <ComboboxOption value="none">Zatím nepřiřazeno</ComboboxOption> : null}
                  {matches.map((option) => (
                    <ComboboxOption key={personKey(option.person)} value={personKey(option.person)}>
                      <span className="block">{option.name}</span>
                      <span className="block text-xs text-muted-foreground">{option.category}</span>
                    </ComboboxOption>
                  ))}
                  {!matches.length ? (
                    <ComboboxEmpty>Nikdo neodpovídá hledání.</ComboboxEmpty>
                  ) : null}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Field>
        </FormLayout>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Smazat roli?"
        description={`Role „${role?.title ?? ''}“ se odebere z obřadu.`}
        keepLabel="Ponechat roli"
        confirmLabel="Smazat roli"
        onConfirm={remove}
      />
    </>
  )
}
